import React, { useCallback, useEffect, memo } from 'react';
import { useField } from 'formik';
import { useParams } from 'react-router-dom';
import { eqProps, filter, complement, propEq } from 'ramda';
import PropTypes from 'prop-types';

import { MEDICAL_HISTORY, usePermissions } from 'permissions';
import Dropzone from 'components/Dropzone';

import Message from 'components/Message';
import QuestionContainer from 'components/QuestionContainer';
import { ID, FILE, FILES, FILE_ID, useMedicalHistoryActions, useAlerts, useWebsocket } from 'store/medicalHistory';

import MultiImage from './MultiImage';
import { DropContainer } from './styles';

const DropzoneForm = ({ readMode }) => {
  const can = usePermissions();
  const [{ value = [] }, , { setValue }] = useField(FILES);
  const { id: patientId } = useParams();
  const { uploadMediaData, deleteSearchableMedia } = useMedicalHistoryActions();
  const { action, success, error, loading, resetAlerts } = useAlerts(uploadMediaData);
  const {
    action: onRemove,
    loading: removeLoading,
    success: removedId,
    resetAlerts: resetRemoved,
  } = useAlerts(deleteSearchableMedia);

  const onDrop = useCallback(
    (files) => {
      if (!loading) {
        action({ files, patientId });
      }
    },
    [action, loading, patientId]
  );
  const updateValues = useCallback((data) => setValue(data, false), [setValue]);
  const updateFile = useCallback(
    (file) => updateValues(value.map((item) => (item?.[FILE]?.[FILE_ID] === file[FILE_ID] ? { ...item, [FILE]: file } : item))),
    [updateValues, value]
  );
  const deleteFile = ({ id, fileData }) => {
    if (id) onRemove({ id, fileData });
  };

  useWebsocket(can.update(MEDICAL_HISTORY), patientId, setValue);

  useEffect(() => {
    if (success) {
      setValue(success);
      resetAlerts();
    }
  }, [resetAlerts, setValue, success]);

  useEffect(() => {
    if (removedId) {
      updateValues(filter(complement(propEq(removedId, ID)), value));
      resetRemoved();
    }
  }, [removedId, resetRemoved, updateValues, value]);

  if (readMode) {
    return (
      <QuestionContainer label="Dokumente">
        <DropContainer readMode={readMode}>
          <MultiImage values={value} readMode={readMode} />
        </DropContainer>
      </QuestionContainer>
    );
  }

  return (
    <QuestionContainer label="Dokumente">
      <DropContainer>
        <MultiImage values={value} updateFile={updateFile} deleteFile={deleteFile} loadingDelete={removeLoading} />
        <Dropzone onDrop={onDrop} loading={loading} />
      </DropContainer>
      {error && (
        <Message type="error" form={1}>
          {error}
        </Message>
      )}
    </QuestionContainer>
  );
};

DropzoneForm.propTypes = {
  readMode: PropTypes.bool.isRequired,
};

export default memo(DropzoneForm, eqProps('readMode'));
